import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { EntityLink } from "@/components/shared/relationships/EntityLink";
import type { PeekEntity } from "@/components/shared/relationships/entity-peek-context";
import {
  createPeekContent,
  LinkedRow,
  PeekHeader,
} from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useCustomerCredit } from "@/features/credits/hooks/use-credit-data";
import { ABSENT_VALUE, formatCurrency, formatEncodedDay, formatDocumentUid } from "@/lib/format";

function resolveCreditApplicationTarget(application: {
  estimate_id: string | null;
  gl_estimates?: { uid: string | null } | null;
  gl_invoices?: { uid: string | null } | null;
  invoice_id: string | null;
}): PeekEntity | null {
  if (application.gl_invoices && application.invoice_id) {
    return {
      id: application.invoice_id,
      label: application.gl_invoices.uid ?? undefined,
      type: "invoice",
    };
  }
  if (application.gl_estimates && application.estimate_id) {
    return {
      id: application.estimate_id,
      label: application.gl_estimates.uid ?? undefined,
      type: "estimate",
    };
  }
  return null;
}

function CreditContent({
  credit,
}: {
  credit: NonNullable<ReturnType<typeof useCustomerCredit>["data"]>;
}) {
  const applications = credit.applications ?? [];

  return (
    <>
      <PeekHeader title={formatDocumentUid(credit.uid, credit.id)} />
      <p className="font-semibold text-2xl">
        <MonoAmount>{formatCurrency(credit.amount)}</MonoAmount>
      </p>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Available:</span>
        <Badge variant={(credit.available_balance ?? 0) > 0 ? "default" : "secondary"}>
          <MonoAmount>{formatCurrency(credit.available_balance ?? 0)}</MonoAmount>
        </Badge>
      </div>
      <Separator />
      <div className="space-y-1">
        <DetailRow label="Date" value={formatEncodedDay(credit.date)} />
        <LinkedRow
          id={credit.gl_accounts?.id}
          label="Account"
          name={credit.gl_accounts?.name}
          type="account"
        />
      </div>
      <Separator />
      <div>
        <h3 className="mb-3 font-medium text-sm">Application History ({applications.length})</h3>
        {applications.length > 0 ? (
          <div className="space-y-2">
            {applications.slice(0, 10).map((application) => {
              const target = resolveCreditApplicationTarget(application);
              return (
                <div className="flex items-center justify-between text-sm" key={application.id}>
                  {target ? (
                    <EntityLink entity={target}>{target.label || "View"}</EntityLink>
                  ) : (
                    <span className="text-muted-foreground">{ABSENT_VALUE}</span>
                  )}
                  <MonoAmount>{formatCurrency(application.amount_applied)}</MonoAmount>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No applications yet</p>
        )}
      </div>
    </>
  );
}

/** Credit quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const CreditPeekContent = createPeekContent({
  emptyMessage: "No credit found",
  render: (credit) => <CreditContent credit={credit} />,
  useDetail: useCustomerCredit,
});

registerPeekType("credit", CreditPeekContent);
